import {z} from 'zod';

/**
 * Normalized source attributions extracted from tool results so the UI can
 * render "Sources" chips beneath an assistant message. KB and web tools
 * return different shapes; this module is the single normalization point.
 */
export const chatSourceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('kb'),
    kbId: z.string().optional(),
    sourceId: z.string().optional(),
    documentId: z.string().optional(),
    name: z.string().optional(),
    snippet: z.string().optional(),
    score: z.number().optional()
  }),
  z.object({
    kind: z.literal('web'),
    url: z.string(),
    title: z.string().optional(),
    snippet: z.string().optional(),
    engine: z.string().optional(),
    publishedDate: z.string().optional()
  })
]);

export type ChatSource = z.infer<typeof chatSourceSchema>;

// KB sources dedup to one chip per *source document*, not per chunk. A single
// search can return many chunks that all came from the same PDF/DOCX/URL;
// rendering one chip per chunk floods the UI (saw 48 chips for one doc).
// documentId is per-chunk and intentionally NOT in the key.
const sourceKey = (s: ChatSource): string => {
  if (s.kind === 'web') return `web:${s.url}`;
  return `kb:${s.sourceId ?? s.documentId ?? ''}`;
};

export const dedupSources = (sources: ChatSource[]): ChatSource[] => {
  const seen = new Map<string, ChatSource>();
  for (const s of sources) {
    const key = sourceKey(s);
    const prev = seen.get(key);
    if (!prev) {
      seen.set(key, s);
      continue;
    }
    // Keep whichever chunk had the higher relevance score so the chip's
    // tooltip snippet is the most relevant one.
    if (s.kind === 'kb' && prev.kind === 'kb') {
      const prevScore = prev.score ?? -Infinity;
      const currScore = s.score ?? -Infinity;
      if (currScore > prevScore) seen.set(key, s);
    }
  }
  return Array.from(seen.values());
};
